import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  CheckCircle, Clock, AlertTriangle, XCircle, CreditCard,
  Calendar, Users, FileText, HardDrive, RefreshCw,
} from 'lucide-react';

const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(v || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—';

const STATUS_CONFIG = {
  active:   { bg: '#dcfce7', color: '#166534', border: '#bbf7d0', icon: CheckCircle, label: 'Active', ring: '#16a34a' },
  trialing: { bg: '#dbeafe', color: '#1e40af', border: '#bfdbfe', icon: Clock, label: 'Free Trial', ring: '#2563eb' },
  pending:  { bg: '#fef3c7', color: '#92400e', border: '#fde68a', icon: Clock, label: 'Pending Approval', ring: '#d97706' },
  expired:  { bg: '#fee2e2', color: '#991b1b', border: '#fecaca', icon: XCircle, label: 'Expired', ring: '#dc2626' },
  cancelled:{ bg: '#f1f5f9', color: '#475569', border: '#e2e8f0', icon: XCircle, label: 'Cancelled', ring: '#64748b' },
};

export default function SubscriptionPage() {
  const navigate = useNavigate();
  const { subscription, user } = useAuth();
  const isSuperAdmin = !user?.company_id && (user?.role_name === 'Administrator' || user?.role_name === 'Superadmin' || user?.id === 1);

  const sub = subscription;

  // Calculate days remaining
  const daysInfo = useMemo(() => {
    if (!sub) return { daysLeft: 0, totalDays: 0, pct: 0, isUrgent: false, isExpired: false };

    const endDate = sub.current_period_end || sub.trial_ends_at;
    const startDate = sub.current_period_start || sub.created_at;

    if (!endDate) return { daysLeft: 0, totalDays: 0, pct: 0, isUrgent: false, isExpired: false };

    const now = new Date();
    const end = new Date(endDate);
    const start = startDate ? new Date(startDate) : new Date(end.getTime() - 30 * 86400000);

    const daysLeft = Math.max(0, Math.ceil((end - now) / 86400000));
    const totalDays = Math.max(1, Math.ceil((end - start) / 86400000));
    const elapsed = totalDays - daysLeft;
    const pct = Math.min(100, Math.max(0, Math.round((elapsed / totalDays) * 100)));
    const isUrgent = daysLeft <= 7 && daysLeft > 0;
    const isExpired = daysLeft === 0 && sub.status !== 'active';

    return { daysLeft, totalDays, pct, isUrgent, isExpired };
  }, [sub]);

  if (!sub) {
    return (
      <div>
        <div className="toolbar">
          <h2>Subscription</h2>
        </div>
        <div className="card" style={{ maxWidth: 600, margin: '40px auto', textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📦</div>
          <h3 style={{ margin: '0 0 8px', color: '#334155' }}>No Active Subscription</h3>
          <p style={{ margin: '0 0 20px', fontSize: 13, color: '#64748b' }}>
            You don't have an active subscription yet. Choose a plan to get started.
          </p>
          <button className="btn btn-primary" onClick={() => navigate('/subscription/renew')}>
            Choose a Plan
          </button>
        </div>
      </div>
    );
  }

  const status = STATUS_CONFIG[sub.status] || STATUS_CONFIG.expired;
  const StatusIcon = status.icon;
  const planName = sub.plan_name || 'Free Trial';
  const price = sub.status === 'trialing' ? 0 : (sub.price_monthly || 0);
  const billingCycle = sub.billing_cycle || 'monthly';

  return (
    <div>        <div className="toolbar">
        <h2>Subscription</h2>
      </div>

      {/* Main Subscription Card */}
      <div className="card" style={{ maxWidth: 680, margin: '0 auto 20px', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg, ${status.ring}22, ${status.ring}08)`,
          borderBottom: `1px solid ${status.border}`,
          padding: '24px 28px',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                Current Plan
              </div>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', lineHeight: 1.1 }}>
                {planName}
              </div>
              <div style={{ fontSize: 14, color: '#475569', marginTop: 4 }}>
                {fmt(price)}/{billingCycle === 'yearly' ? 'yr' : 'mo'}
              </div>
            </div>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '6px 14px', borderRadius: 20,
              background: status.bg, color: status.color,
              fontSize: 12, fontWeight: 700,
            }}>
              <StatusIcon size={14} />
              {status.label}
            </div>
          </div>

          {/* Days Remaining — prominent display */}
          <div style={{ marginTop: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
              <div style={{
                fontSize: 32, fontWeight: 800,
                color: daysInfo.isUrgent ? '#dc2626' : daysInfo.isExpired ? '#64748b' : '#0f172a',
                lineHeight: 1,
              }}>
                {daysInfo.daysLeft}
                <span style={{ fontSize: 14, fontWeight: 600, color: '#64748b', marginLeft: 4 }}>
                  day{daysInfo.daysLeft !== 1 ? 's' : ''} remaining
                </span>
              </div>
              {sub.current_period_end && (
                <div style={{ fontSize: 12, color: '#64748b' }}>
                  Expires: <strong style={{ color: '#334155' }}>{fmtDate(sub.current_period_end)}</strong>
                </div>
              )}
            </div>

            {/* Progress bar */}
            <div style={{ background: '#e2e8f0', borderRadius: 6, height: 8, overflow: 'hidden' }}>
              <div style={{
                height: '100%', borderRadius: 6,
                width: `${100 - daysInfo.pct}%`,
                background: daysInfo.isUrgent
                  ? 'linear-gradient(90deg, #f59e0b, #dc2626)'
                  : daysInfo.isExpired
                    ? '#94a3b8'
                    : `linear-gradient(90deg, ${status.ring}, ${status.ring}cc)`,
                transition: 'width 0.5s ease',
              }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 10, color: '#94a3b8' }}>
              <span>{fmtDate(sub.current_period_start || sub.created_at)}</span>
              <span>{fmtDate(sub.current_period_end)}</span>
            </div>
          </div>
        </div>

        {/* Pending Approval banner */}
        {sub.status === 'pending' && (
          <div style={{
            padding: '14px 28px',
            background: '#fffbeb',
            borderBottom: '1px solid #fde68a',
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <Clock size={16} style={{ color: '#d97706', flexShrink: 0 }} />
            <span style={{ fontSize: 13, color: '#92400e', flex: 1 }}>
              <strong>Subscription pending LedgerPro Accounting Sales approval.</strong> Your 14-day free trial is active. You have full access to all {planName} features during the trial period.
            </span>
          </div>
        )}

        {/* Trial info banner */}
        {sub.status === 'trialing' && sub.trial_ends_at && (
          <div style={{
            padding: '12px 28px',
            background: '#eff6ff',
            borderBottom: '1px solid #bfdbfe',
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <Clock size={16} style={{ color: '#2563eb', flexShrink: 0 }} />
            <span style={{ fontSize: 13, color: '#1e40af', flex: 1 }}>
              <strong>Free trial active</strong> — ends {fmtDate(sub.trial_ends_at)}. Subscribe to continue after the trial period.
            </span>
          </div>
        )}

        {/* Payment Required banner */}
        {sub.status === 'active' && sub.confirmed_by && (
          <div style={{
            padding: '12px 28px',
            background: '#f0fdf4',
            borderBottom: '1px solid #bbf7d0',
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <CheckCircle size={16} style={{ color: '#16a34a', flexShrink: 0 }} />
            <span style={{ fontSize: 13, color: '#166534', flex: 1 }}>
              <strong>Subscription confirmed</strong> — Your subscription is active and payment has been verified.
            </span>
          </div>
        )}

        {/* Warning banner for urgent/expired */}
        {(daysInfo.isUrgent || daysInfo.isExpired) && (
          <div style={{
            padding: '12px 28px',
            background: daysInfo.isExpired ? '#fef2f2' : '#fffbeb',
            borderBottom: `1px solid ${daysInfo.isExpired ? '#fecaca' : '#fde68a'}`,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <AlertTriangle size={16} style={{ color: daysInfo.isExpired ? '#dc2626' : '#d97706', flexShrink: 0 }} />
            <span style={{ fontSize: 13, color: daysInfo.isExpired ? '#991b1b' : '#92400e', flex: 1 }}>
              {daysInfo.isExpired
                ? 'Your subscription has expired. Please renew to continue using all features.'
                : `Your subscription expires in ${daysInfo.daysLeft} day${daysInfo.daysLeft !== 1 ? 's' : ''}. Renew now to avoid service interruption.`}
            </span>
          </div>
        )}

        {/* Details grid */}
        <div style={{ padding: '20px 28px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 32px' }}>
            <DetailRow icon={Calendar} label="Billing Cycle" value={billingCycle === 'yearly' ? 'Yearly' : 'Monthly'} />
            <DetailRow icon={CreditCard} label="Plan Price" value={`${fmt(price)}/${billingCycle === 'yearly' ? 'year' : 'month'}`} />
            <DetailRow icon={Calendar} label="Start Date" value={fmtDate(sub.current_period_start || sub.created_at)} />
            <DetailRow icon={Calendar} label={sub.status === 'trialing' ? 'Trial Ends' : 'Next Billing'} value={fmtDate(sub.current_period_end || sub.trial_ends_at)} />
            <DetailRow icon={Users} label="Max Users" value={sub.max_users ?? '—'} />
            <DetailRow icon={FileText} label="Invoices / Month" value={sub.max_invoices_month === -1 ? 'Unlimited' : sub.max_invoices_month ?? '—'} />
            <DetailRow icon={HardDrive} label="Storage" value={sub.max_storage_mb != null ? `${sub.max_storage_mb} MB` : '—'} />
            <DetailRow icon={RefreshCw} label="Auto-Renew" value={sub.auto_renew ? 'Yes' : 'No'} />
          </div>
        </div>

        {/* Actions */}
        <div style={{ padding: '16px 28px', borderTop: '1px solid #e2e8f0', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            className="btn btn-outline"
            onClick={() => navigate('/subscription/renew')}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            Change Plan
          </button>
          {(daysInfo.isUrgent || daysInfo.isExpired || sub.status === 'trialing') && (
            <button
              className="btn btn-primary"
              onClick={() => navigate('/subscription/renew')}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <CreditCard size={14} />
              {daysInfo.isExpired ? 'Renew Subscription' : sub.status === 'trialing' ? 'Subscribe Now' : 'Renew Now'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailRow({ icon: Icon, label, value }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0' }}>
      <div style={{
        width: 32, height: 32, borderRadius: 8, background: '#f1f5f9',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Icon size={15} style={{ color: '#64748b' }} />
      </div>
      <div>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4 }}>
          {label}
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
          {value}
        </div>
      </div>
    </div>
  );
}
